import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ListContact, ListContactsRequest } from '../../types/ListContact';
import { CreateListRequest, ListItem } from '../../types/Lists';
import {
    requestCreateGroupList,
    requestDeleteGroupList,
    requestGroupContacts,
    requestGroupsList,
} from '../GroupList/GroupList';

export function useGroupsList() {
    return useQuery<ListItem[], Error>({
        queryKey: ['groups'],
        queryFn: async ({ signal }) => await requestGroupsList(signal),
    });
}

export function useCreateGroupList() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: CreateListRequest) => requestCreateGroupList(data),
        onMutate: async (request: CreateListRequest) => {
            await queryClient.cancelQueries({ queryKey: ['groups'] });
            const currentGroupList = queryClient.getQueryData<ListItem[]>(['groups']);

            return { currentGroupList };
        },
        onError: (error, variables, context) => {
            queryClient.invalidateQueries({ queryKey: ['groups'] });
        },
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: ['groups'] });
        },
    });
}

export function useDeleteGroupList() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (listId: number) => requestDeleteGroupList(listId),
        onMutate: async id => {
            await queryClient.cancelQueries({ queryKey: ['groups'] });
            const currentGroupList = queryClient.getQueryData<ListItem[]>(['groups']);
            return { currentGroupList };
        },
        onError: (error, variables, context) => {
            queryClient.invalidateQueries({ queryKey: ['groups'] });
        },
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: ['groups'] });
        },
    });
}

export function useGetGroupContacts(groupId: number) {
    const contactListReq: ListContactsRequest = {
        listId: groupId,
        contactIds: [],
        filters: [],
    };

    return useQuery<ListContact[], Error>({
        queryKey: [`group-${groupId}`],
        queryFn: async ({ signal }) => await requestGroupContacts(contactListReq, signal),
    });
}
